import type { EncounterWorkNote } from '../types/encounterWork';

/**
 * A note the client shows before the server has taken it: the cache entry the
 * add-note mutation writes optimistically, and the marker the card reads to
 * draw it dimmed until the server's own note takes its place.
 *
 * The marker is the id. Server ids are UUIDs; a provisional id carries this
 * prefix and never leaves the client. Given the mutation's `requestId` the id
 * is that, so the two passes the app's re-entrant middleware makes for one
 * request name the same provisional note and a second request names another.
 */
const PROVISIONAL_ID_PREFIX = 'provisional:';

export interface ProvisionalNoteArgs {
  body: string;
  /** The mutation's request id; absent, a random one stands in. */
  requestId?: string;
  /** The signed-in person's display name, or null before the user read lands. */
  authorName: string | null;
  /** The signed-in person's principal id, or null before the user read lands. */
  authorPrincipalId: string | null;
  claimId: string | null;
  now?: () => Date;
}

export function makeProvisionalNote({
  body,
  requestId,
  authorName,
  authorPrincipalId,
  claimId,
  now = () => new Date(),
}: ProvisionalNoteArgs): EncounterWorkNote {
  const at = now();
  const iso = at.toISOString();
  return {
    id: `${PROVISIONAL_ID_PREFIX}${
      requestId ?? `${at.getTime()}-${Math.random().toString(36).slice(2, 8)}`
    }`,
    body,
    author_name: authorName,
    author_principal_id: authorPrincipalId,
    claim_id: claimId,
    created_at: iso,
    updated_at: iso,
    edited_at: null,
    /* Closed, not invented: the server owns the window and its copy brings
       the real `editable_until`. Until then there is no id to edit or delete
       by, so a note in flight offers neither. */
    editable_until: iso,
  };
}

export const isProvisionalNote = (note: Pick<EncounterWorkNote, 'id'>): boolean =>
  note.id.startsWith(PROVISIONAL_ID_PREFIX);
